import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';

/**
 * Vertex-colour ambient occlusion: dark where geometry meets the ground, lifted along
 * the top edge.
 *
 * ── Why this and not a light, a texture or a shader ─────────────────────────
 *
 * The complaint it answers (owner's report, 2026-08-11) is *visual monotony, missing
 * depth, flat lighting* — buildings meeting the cobbles on a sharp flat line, with
 * nowhere for the eye to rest. That is an **ambient occlusion** problem, and AO is the
 * one lighting cue a scene of merged boxes cannot get any other way: the sun is a single
 * directional light, so every face at the same angle is lit identically whether it is in
 * the open or jammed against a neighbour.
 *
 * Baking it into vertex colours costs **zero bytes on the wire and zero draw calls**. It
 * is not a texture, so it needs no atlas, no UVs and no download; it is not a light, so
 * it adds nothing per-frame; and it survives `Mesh.MergeMeshes`, which is what lets the
 * whole town stay one mesh per material.
 *
 * It also survives the pixelate post-process, which most detail does not — at three
 * screen pixels per texel and fourteen colour levels per channel, a normal map or a
 * specular highlight is averaged into nothing. A vertical gradient across a whole wall
 * is exactly the scale that reduction preserves. That mattered while the pass was on by
 * default and still matters: it is an option, so this has to read either way.
 *
 * ── The shape of it ─────────────────────────────────────────────────────────
 *
 * A single vertical ramp, because these are boxes standing on a floor and the contact
 * shadow is the whole point. `floor` is the multiplier at the base and `ceiling` at the
 * top, interpolated on the vertex's own Y within the mesh's height. Above 1 lifts rather
 * than darkens, which is what gives a roof edge the ambient catch that stops a cube
 * reading as a mathematical solid.
 */
export interface ShadeRamp {
  /** Multiplier at the lowest vertex. Below 1 darkens. */
  floor: number;
  /** Multiplier at the highest vertex. Above 1 lifts. */
  ceiling: number;
  /**
   * How far up the mesh the darkening has faded out, as a fraction of its height.
   *
   * The contact shadow belongs at the *bottom*, not spread evenly: a ramp running the
   * whole height reads as a gradient paint job, where one that resolves in the lowest
   * third reads as the ground occluding the wall. 1 gives the plain linear ramp.
   */
  reach: number;
}

/** A contact shadow at the foot of a wall, and a lift along its top edge. */
export const STRUCTURE_SHADE: ShadeRamp = { floor: 0.42, ceiling: 1.12, reach: 0.55 };

/**
 * A roof's ramp: no contact shadow, all lift.
 *
 * A roof slab is a fifth of a wall's height, so a ramp tuned for a wall resolves across
 * a few centimetres and reads as a hard band. What a roof needs is the opposite half of
 * the effect — the top face brighter than the eaves, so a terrace of houses reads as
 * separate caps rather than one extruded mass.
 */
export const ROOF_SHADE: ShadeRamp = { floor: 0.78, ceiling: 1.2, reach: 1 };

/**
 * How far a single building may drift from its neighbours, either way.
 *
 * ±14% of value and ±18% of height. Deliberately small: the complaint is *uniform
 * dark-gray blocks*, not *a terrace of mismatched houses* — past about a fifth the row
 * stops reading as one street and starts reading as a bug. It is enough to break the
 * repetition that makes a merged mesh look extruded.
 */
export const TINT_SPREAD = 0.14;
export const HEIGHT_SPREAD = 0.18;

/**
 * A building's own value multiplier, from a hash of its tile.
 *
 * **A hash of the coordinates, never a random number.** Zone geometry is rebuilt from the
 * ZONE frame every time a player arrives, so anything drawn from an RNG would give the same
 * street a different look on each visit — and two players standing in one town would see
 * different buildings. The same rule the packs and `Step` follow: randomness comes from
 * seeded state, never from a global source.
 */
export function buildingTint(hash: number): number {
  return 1 + (hash * 2 - 1) * TINT_SPREAD;
}

/** The same, for how tall it stands. */
export function buildingHeightScale(hash: number): number {
  return 1 + (hash * 2 - 1) * HEIGHT_SPREAD;
}

/**
 * The tint at a height, as a multiplier on the material's diffuse.
 *
 * Pure, and separated from the mesh so it can be reasoned about and tested without a
 * scene: every assertion about how this *looks* is an assertion about this function.
 *
 * `y` and the bounds are world units. A degenerate mesh — every vertex at one height,
 * which a flat quad is — yields the ceiling rather than dividing by zero: a floor tile
 * has no foot to occlude, and darkening it to `floor` would put a shadow under nothing.
 */
export function shadeAt(y: number, minY: number, maxY: number, ramp: ShadeRamp): number {
  const span = maxY - minY;
  if (span <= 0) return ramp.ceiling;

  const t = Math.min(1, Math.max(0, (y - minY) / span));
  // The lift is linear across the whole height; the darkening resolves within `reach`.
  const lift = ramp.ceiling >= 1 ? 1 + (ramp.ceiling - 1) * t : ramp.ceiling;
  const occluded = ramp.reach <= 0 ? 1 : Math.min(1, t / ramp.reach);
  return (ramp.floor + (1 - ramp.floor) * occluded) * lift;
}

/**
 * Writes the ramp into a mesh's vertex colours.
 *
 * **Before merging, and before the material is set.** `Mesh.MergeMeshes` keeps a colour
 * buffer only when every source has one, so a single box without it silently drops the
 * whole town's shading — which is why this is applied per box rather than to the merged
 * result, where the world-Y of a vertex would no longer say which building it belongs to.
 *
 * A no-op on a mesh with no positions, so a builder that produced nothing does not throw.
 *
 * `tint` scales the whole ramp, which is how one building comes out slightly darker than
 * its neighbour without a second material: the town merges into one mesh per material, so
 * per-building colour has nowhere else to live. See `buildingTint`.
 */
export function bakeVertexShade(mesh: Mesh, ramp: ShadeRamp = STRUCTURE_SHADE, tint = 1): void {
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  if (!positions || positions.length === 0) return;

  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 1; i < positions.length; i += 3) {
    const y = positions[i] as number;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  const colours = new Float32Array((positions.length / 3) * 4);
  for (let v = 0, i = 1; i < positions.length; v += 4, i += 3) {
    const shade = shadeAt(positions[i] as number, minY, maxY, ramp) * tint;
    colours[v] = shade;
    colours[v + 1] = shade;
    colours[v + 2] = shade;
    colours[v + 3] = 1;
  }

  mesh.setVerticesData(VertexBuffer.ColorKind, colours, false);
  // Set explicitly although Babylon already defaults it to true: the flag is what decides
  // whether the buffer above is read at all, and a default is a thing that can change
  // under you. Stating it costs nothing and makes the dependency visible where the data
  // is written rather than in a release note.
  mesh.useVertexColors = true;
}
